import React from 'react';
import { motion } from 'framer-motion';

const CipherForm = ({
  inputText,
  setInputText,
  cipherType,
  setCipherType,
  cipherKey,
  setCipherKey,
  onEncrypt,
  onDecrypt,
  onClear,
  isLoading = false
}) => {
  const requiresKey = !['polybius'].includes(cipherType);

  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Cipher Input</h2>

      <div className="space-y-4">
        {/* Text Input */}
        <div>
          <label htmlFor="inputText" className="block text-sm font-medium text-gray-700 mb-2">
            Text to Process
          </label>
          <motion.textarea
            id="inputText"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter text to encrypt or decrypt..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={6}
            whileFocus={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          />
        </div>

        {/* Cipher Type Selection */}
        <div>
          <label htmlFor="cipherType" className="block text-sm font-medium text-gray-700 mb-2">
            Cipher Type
          </label>
            <motion.select
              id="cipherType"
              value={cipherType}
              onChange={(e) => setCipherType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              whileFocus={{ scale: 1.01 }}
            >
              <option value="caesar">Caesar Cipher</option>
              <option value="polybius">Polybius Cipher</option>
              <option value="railfence">Rail Fence Cipher</option>
              <option value="transposition">Transposition Cipher</option>
              <option value="monoalphabetic">Monoalphabetic Cipher</option>
              <option value="vigenere">Vigenère Cipher</option>
              <option value="playfair">Playfair Cipher</option>
              <option value="hill">Hill Cipher</option>
              <option value="rowColumnTransposition">Row/Column Transposition</option>
            </motion.select>
        </div>

        {/* Key Input (conditional) */}
        {requiresKey && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <label htmlFor="cipherKey" className="block text-sm font-medium text-gray-700 mb-2">
              {cipherType === 'caesar' ? 'Shift Value' : 
               cipherType === 'railfence' ? 'Number of Rails' :
               cipherType === 'hill' ? 'Key Matrix (e.g., "2x2:1,2,3,4")' :
               cipherType === 'monoalphabetic' ? '26-letter Key (A-Z permutation)' :
               'Key'}
            </label>
            <motion.input
              id="cipherKey"
              type="text"
              value={cipherKey}
              onChange={(e) => setCipherKey(e.target.value)}
              placeholder={
                cipherType === 'caesar' ? 'Enter shift value (0-25)...' :
                cipherType === 'railfence' ? 'Enter number of rails...' :
                cipherType === 'hill' ? 'Enter matrix (e.g., "2x2:1,2,3,4")...' :
                cipherType === 'monoalphabetic' ? 'Enter 26-letter permutation...' :
                'Enter key...'
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              whileFocus={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <motion.button
            onClick={onEncrypt}
            disabled={isLoading || !inputText.trim() || (requiresKey && !cipherKey.trim())}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? '⏳ Processing...' : '🔐 Encrypt'}
          </motion.button>

          <motion.button
            onClick={onDecrypt}
            disabled={isLoading || !inputText.trim() || (requiresKey && !cipherKey.trim())}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? '⏳ Processing...' : '🔓 Decrypt'}
          </motion.button>

          <motion.button
            onClick={onClear}
            disabled={isLoading}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            Clear
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default CipherForm;
